import { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import { ChevronLeft, ChevronRight, Check, Plus, Minus, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/contexts/CartContext";
import { CHICKEN_PIECES, BISCUITS, DIPPING_SAUCES, formatPrice, EXTRA_SAUCE_PRICE, BRAND_COLORS } from "@/lib/menuData";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function KrunchBoxWizard({ open, onClose, availableSides, menuItems, type }) {
  const isPieces = type === "pieces";
  const isTenders = type === "tenders";

  const [step, setStep] = useState(0);
  const [selectedMain, setSelectedMain] = useState(null);
  const [pieceQuantities, setPieceQuantities] = useState({});
  const [selectedBiscuit, setSelectedBiscuit] = useState(null);
  const [selectedSide, setSelectedSide] = useState(null);
  const [sauceQuantities, setSauceQuantities] = useState({});
  
  // Biscuit availability state
  const [biscuitAvailable, setBiscuitAvailable] = useState(true);
  const [loadingBiscuit, setLoadingBiscuit] = useState(true);
  
  const { addItem } = useCart();

  // Fetch biscuit availability on mount
  useEffect(() => {
    const fetchBiscuitAvailability = async () => {
      try {
        const res = await axios.get(`${API}/settings/biscuit-availability`);
        setBiscuitAvailable(res.data.available);
      } catch (e) {
        console.error("Failed to fetch biscuit availability, defaulting to available");
        setBiscuitAvailable(true);
      } finally {
        setLoadingBiscuit(false);
      }
    };
    if (open) {
      fetchBiscuitAvailability();
    }
  }, [open]);

  // Dynamic steps based on biscuit availability
  const steps = useMemo(() => {
    if (isPieces) {
      if (biscuitAvailable) {
        return ["Choose Size", "Choose Pieces", "Choose Biscuit", "Choose Side", "Choose Sauces"];
      } else {
        return ["Choose Size", "Choose Pieces", "Choose Side", "Choose Sauces"];
      }
    } else {
      // Tenders
      if (biscuitAvailable) {
        return ["Choose Size", "Choose Biscuit", "Choose Side", "Choose Sauces"];
      } else {
        return ["Choose Size", "Choose Side", "Choose Sauces"];
      }
    }
  }, [isPieces, biscuitAvailable]);

  const mainOptions = useMemo(() => {
    const cat = isPieces ? "krunch_box_pieces" : "krunch_box_tenders";
    return (menuItems || []).filter((i) => i.category === cat);
  }, [menuItems, isPieces]);

  const reset = () => {
    setStep(0);
    setSelectedMain(null);
    setPieceQuantities({});
    setSelectedBiscuit(null);
    setSelectedSide(null);
    setSauceQuantities({});
  };

  const handleClose = () => { reset(); onClose(); };

  const getPieceCount = () => {
    if (!selectedMain) return 0;
    const match = selectedMain.size?.match(/(\d+)/);
    return match ? parseInt(match[1]) : 0;
  };

  const getCurrentPieceTotal = () => {
    return Object.values(pieceQuantities).reduce((sum, qty) => sum + qty, 0);
  };

  const updatePieceQuantity = (pieceId, delta) => {
    const total = getCurrentPieceTotal();
    const current = pieceQuantities[pieceId] || 0;
    const newQty = current + delta;
    
    if (newQty < 0) return;
    if (delta > 0 && total >= getPieceCount()) return;
    
    setPieceQuantities(prev => ({
      ...prev,
      [pieceId]: newQty
    }));
  };

  // Calculate free sauces - Tenders get 1 per 3 pieces, Chicken pieces get 0
  const freeSauceCount = useMemo(() => {
    if (!selectedMain) return 0;
    
    // Only tenders get free sauces
    if (!isTenders) return 0;
    
    // Calculate based on piece count for tenders
    const sizeMatch = selectedMain.size?.match(/(\d+)/);
    if (sizeMatch) {
      const pieces = parseInt(sizeMatch[1], 10);
      return Math.floor(pieces / 3); // 1 free per 3 pieces
    }
    return 0;
  }, [selectedMain, isTenders]);
  
  const getTotalSauceQuantity = () => {
    return Object.values(sauceQuantities).reduce((sum, qty) => sum + qty, 0);
  };

  const updateSauceQuantity = (sauceId, delta) => {
    const current = sauceQuantities[sauceId] || 0;
    const newQty = current + delta;
    if (newQty < 0) return;
    
    setSauceQuantities(prev => ({
      ...prev,
      [sauceId]: newQty
    }));
  };

  const getExtraSauceCost = () => {
    const total = getTotalSauceQuantity();
    const extraCount = Math.max(0, total - freeSauceCount);
    return extraCount * EXTRA_SAUCE_PRICE;
  };

  // Dynamic step indices based on biscuit availability
  const getStepIndex = (stepName) => {
    return steps.indexOf(stepName);
  };

  const canNext = () => {
    const currentStepName = steps[step];
    
    switch (currentStepName) {
      case "Choose Size":
        return !!selectedMain;
      case "Choose Pieces":
        return getCurrentPieceTotal() === getPieceCount();
      case "Choose Biscuit":
        return !!selectedBiscuit;
      case "Choose Side":
        return !!selectedSide;
      case "Choose Sauces":
        return true; // Sauces are optional
      default:
        return false;
    }
  };

  const handleConfirm = () => {
    if (!selectedMain || !selectedSide) return;
    if (biscuitAvailable && !selectedBiscuit) return;
    
    const label = isPieces ? "Chicken Pieces" : "Tenders";
    let displayName = `${label} ${selectedMain.size} Krunch Box`;
    
    // Remove "Biscuit" from name if biscuits unavailable
    if (!biscuitAvailable) {
      displayName = displayName.replace(" Krunch Box", " Box (No Biscuit)");
    }

    const customizations = {
      side: selectedSide,
    };
    
    // Add biscuit only if available
    if (biscuitAvailable && selectedBiscuit) {
      customizations.biscuit = selectedBiscuit;
    }
    
    // Add piece selection for chicken pieces
    if (isPieces && Object.keys(pieceQuantities).length > 0) {
      const pieceNames = CHICKEN_PIECES
        .filter((p) => pieceQuantities[p.id] > 0)
        .map((p) => `${pieceQuantities[p.id]}x ${p.name}`)
        .join(", ");
      customizations.pieces = pieceNames;
    }

    // Add sauce selection
    const totalSauces = getTotalSauceQuantity();
    if (totalSauces > 0) {
      const sauceList = DIPPING_SAUCES
        .filter((s) => sauceQuantities[s.id] > 0)
        .map((s) => `${sauceQuantities[s.id]}x ${s.name}`)
        .join(", ");
      const extraCost = getExtraSauceCost();
      customizations.sauces = extraCost > 0 
        ? `${sauceList} (+$${extraCost.toFixed(2)})`
        : `${sauceList} (Free)`;
    } else {
      customizations.sauces = "No sauce";
    }

    const finalPrice = selectedMain.price + getExtraSauceCost();

    addItem({
      name: displayName,
      concept: "chicken",
      category: isPieces ? "krunch_box_pieces" : "krunch_box_tenders",
      price: finalPrice,
      quantity: 1,
      customizations,
    });
    toast.success(`${displayName} added to cart`);
    handleClose();
  };

  const totalSteps = steps.length;
  const currentStepName = steps[step];

  if (loadingBiscuit) {
    return (
      <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
        <DialogContent className="max-w-md rounded-2xl p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin h-8 w-8 border-4 border-slate-200 border-t-orange-500 rounded-full" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-md rounded-2xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }} className="text-lg">
            {isPieces ? "Chicken Pieces Krunch Box" : "Tenders Krunch Box"}
          </DialogTitle>
          <DialogDescription>Step {step + 1} of {totalSteps}: {currentStepName}</DialogDescription>
        </DialogHeader>

        {/* Biscuit unavailable banner */}
        {!biscuitAvailable && (
          <div className="mx-6 mb-2 p-2 rounded-lg flex items-center gap-2" style={{ backgroundColor: "#FEF3C7" }}>
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <span className="text-xs text-amber-800">Biscuits currently unavailable</span>
          </div>
        )}

        {/* Step indicators */}
        <div className="flex items-center gap-1 px-6 pb-3">
          {steps.map((_, i) => (
            <div key={i} className="flex-1">
              <div 
                className="h-1.5 rounded-full transition-all" 
                style={{ backgroundColor: i <= step ? BRAND_COLORS.sunsetOrange : "#E5E7EB" }} 
              />
            </div>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-4 min-h-0">
          {/* Step: Choose Size */}
          {currentStepName === "Choose Size" && (
            <div className="space-y-2" data-testid="wizard-step-size">
              {mainOptions.map((item) => {
                const label = `${item.name} ${item.size}`;
                const isSelected = selectedMain?.id === item.id;
                // Show free sauce info for tenders
                const sizeMatch = item.size?.match(/(\d+)/);
                const pieceCount = sizeMatch ? parseInt(sizeMatch[1], 10) : 0;
                const freeSauces = isTenders ? Math.floor(pieceCount / 3) : 0;
                
                return (
                  <button key={item.id} data-testid={`main-option-${item.id}`}
                    className="w-full p-3 rounded-xl border-2 text-left transition-all flex items-center justify-between"
                    style={{
                      borderColor: isSelected ? BRAND_COLORS.sunsetOrange : "#E5E7EB",
                      backgroundColor: isSelected ? BRAND_COLORS.sunsetOrange + "10" : "white"
                    }}
                    onClick={() => { setSelectedMain(item); setPieceQuantities({}); }}
                  >
                    <div>
                      <p className="font-semibold text-sm" style={{ color: BRAND_COLORS.darkSlate }}>{label}</p>
                      {isTenders && freeSauces > 0 && (
                        <p className="text-xs mt-0.5" style={{ color: BRAND_COLORS.tropicalAqua }}>
                          {freeSauces} Free Sauce{freeSauces > 1 ? "s" : ""}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm" style={{ color: BRAND_COLORS.sunsetOrange }}>{formatPrice(item.price)}</span>
                      {isSelected && <Check className="h-4 w-4" style={{ color: BRAND_COLORS.sunsetOrange }} />}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Step: Choose Pieces (only for chicken pieces) */}
          {currentStepName === "Choose Pieces" && (
            <div className="space-y-2" data-testid="wizard-step-pieces">
              <p className="text-sm mb-3" style={{ color: BRAND_COLORS.darkSlate + "99" }}>
                Select {getPieceCount()} piece{getPieceCount() > 1 ? "s" : ""} ({getCurrentPieceTotal()}/{getPieceCount()})
              </p>
              {CHICKEN_PIECES.map((piece) => {
                const qty = pieceQuantities[piece.id] || 0;
                const canAdd = getCurrentPieceTotal() < getPieceCount();
                return (
                  <div key={piece.id} data-testid={`piece-option-${piece.id}`}
                    className="flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-white"
                  >
                    <span className="font-medium text-sm" style={{ color: BRAND_COLORS.darkSlate }}>{piece.name}</span>
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`piece-minus-${piece.id}`}
                        className="w-8 h-8 rounded-full flex items-center justify-center transition-colors disabled:opacity-30"
                        style={{ backgroundColor: "#E8E8E8" }}
                        onClick={() => updatePieceQuantity(piece.id, -1)}
                        disabled={qty === 0}
                      >
                        <Minus className="h-4 w-4" style={{ color: BRAND_COLORS.darkSlate }} />
                      </button>
                      <span className="text-sm font-bold min-w-[24px] text-center" style={{ color: BRAND_COLORS.darkSlate }}>
                        {qty}
                      </span>
                      <button
                        data-testid={`piece-plus-${piece.id}`}
                        className="w-8 h-8 rounded-full flex items-center justify-center transition-colors disabled:opacity-30"
                        style={{ backgroundColor: canAdd ? BRAND_COLORS.tropicalAqua + "30" : "#E8E8E8" }}
                        onClick={() => updatePieceQuantity(piece.id, 1)}
                        disabled={!canAdd}
                      >
                        <Plus className="h-4 w-4" style={{ color: canAdd ? BRAND_COLORS.tropicalAqua : BRAND_COLORS.darkSlate }} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Step: Choose Biscuit */}
          {currentStepName === "Choose Biscuit" && (
            <div className="space-y-2" data-testid="wizard-step-biscuit">
              {BISCUITS.map((b) => {
                const isSelected = selectedBiscuit === b.name;
                return (
                  <button key={b.id} data-testid={`biscuit-option-${b.id}`}
                    className="w-full p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between"
                    style={{
                      borderColor: isSelected ? BRAND_COLORS.sunsetOrange : "#E5E7EB",
                      backgroundColor: isSelected ? BRAND_COLORS.sunsetOrange + "10" : "white"
                    }}
                    onClick={() => setSelectedBiscuit(b.name)}
                  >
                    <p className="font-semibold text-sm" style={{ color: BRAND_COLORS.darkSlate }}>{b.name}</p>
                    {isSelected && <Check className="h-4 w-4" style={{ color: BRAND_COLORS.sunsetOrange }} />}
                  </button>
                );
              })}
            </div>
          )}

          {/* Step: Choose Side */}
          {currentStepName === "Choose Side" && (
            <div className="space-y-2" data-testid="wizard-step-side">
              {availableSides.length === 0 ? (
                <p className="text-sm text-center py-4" style={{ color: BRAND_COLORS.darkSlate + "66" }}>No sides currently available</p>
              ) : (
                availableSides.map((s) => {
                  const isSelected = selectedSide === s.name;
                  return (
                    <button key={s.name} data-testid={`side-option-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className="w-full p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between"
                      style={{
                        borderColor: isSelected ? BRAND_COLORS.sunsetOrange : "#E5E7EB",
                        backgroundColor: isSelected ? BRAND_COLORS.sunsetOrange + "10" : "white"
                      }}
                      onClick={() => setSelectedSide(s.name)}
                    >
                      <p className="font-semibold text-sm" style={{ color: BRAND_COLORS.darkSlate }}>{s.name}</p>
                      {isSelected && <Check className="h-4 w-4" style={{ color: BRAND_COLORS.sunsetOrange }} />}
                    </button>
                  );
                })
              )}
            </div>
          )}

          {/* Step: Choose Sauces */}
          {currentStepName === "Choose Sauces" && (
            <div className="space-y-3" data-testid="wizard-step-sauces">
              {/* Free sauce info banner */}
              <div className="p-3 rounded-xl" style={{ backgroundColor: BRAND_COLORS.tropicalAqua + "20" }}>
                <p className="text-sm text-center" style={{ color: BRAND_COLORS.deepTeal }}>
                  {freeSauceCount > 0 ? (
                    <>
                      <strong>{freeSauceCount} Free Sauce{freeSauceCount !== 1 ? "s" : ""}</strong>
                      {getTotalSauceQuantity() > freeSauceCount && (
                        <span> • Extra: +${getExtraSauceCost().toFixed(2)}</span>
                      )}
                    </>
                  ) : (
                    <span>Sauces: <strong>${EXTRA_SAUCE_PRICE.toFixed(2)}</strong> each</span>
                  )}
                </p>
              </div>

              <p className="text-xs" style={{ color: BRAND_COLORS.darkSlate + "99" }}>
                Selected: {getTotalSauceQuantity()} sauce{getTotalSauceQuantity() !== 1 ? "s" : ""} 
                {freeSauceCount > 0 && getTotalSauceQuantity() > freeSauceCount && ` (${getTotalSauceQuantity() - freeSauceCount} extra @ $0.79 each)`}
                {freeSauceCount === 0 && getTotalSauceQuantity() > 0 && ` ($${getExtraSauceCost().toFixed(2)})`}
              </p>

              {DIPPING_SAUCES.map((sauce) => {
                const qty = sauceQuantities[sauce.id] || 0;
                const runningTotal = DIPPING_SAUCES
                  .slice(0, DIPPING_SAUCES.indexOf(sauce))
                  .reduce((sum, s) => sum + (sauceQuantities[s.id] || 0), 0);
                
                return (
                  <div key={sauce.id} data-testid={`sauce-option-${sauce.id}`}
                    className="flex items-center justify-between p-3 rounded-xl border bg-white"
                    style={{ borderColor: qty > 0 ? BRAND_COLORS.sunsetOrange : "#E5E7EB" }}
                  >
                    <div>
                      <span className="font-medium text-sm" style={{ color: BRAND_COLORS.darkSlate }}>{sauce.name}</span>
                      {qty > 0 && freeSauceCount > 0 && (
                        <span className="text-xs ml-2" style={{ color: runningTotal + qty <= freeSauceCount ? BRAND_COLORS.tropicalAqua : BRAND_COLORS.sunsetOrange }}>
                          {runningTotal < freeSauceCount ? 
                            (runningTotal + qty <= freeSauceCount ? "Free" : `${Math.min(qty, freeSauceCount - runningTotal)} Free, ${qty - (freeSauceCount - runningTotal)} @ $0.79`) 
                            : `@ $0.79 each`}
                        </span>
                      )}
                      {qty > 0 && freeSauceCount === 0 && (
                        <span className="text-xs ml-2" style={{ color: BRAND_COLORS.sunsetOrange }}>@ $0.79 each</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`sauce-minus-${sauce.id}`}
                        className="w-8 h-8 rounded-full flex items-center justify-center transition-colors disabled:opacity-30"
                        style={{ backgroundColor: "#E8E8E8" }}
                        onClick={() => updateSauceQuantity(sauce.id, -1)}
                        disabled={qty === 0}
                      >
                        <Minus className="h-4 w-4" style={{ color: BRAND_COLORS.darkSlate }} />
                      </button>
                      <span className="text-sm font-bold min-w-[24px] text-center" style={{ color: BRAND_COLORS.darkSlate }}>
                        {qty}
                      </span>
                      <button
                        data-testid={`sauce-plus-${sauce.id}`}
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: BRAND_COLORS.sunsetOrange + "20" }}
                        onClick={() => updateSauceQuantity(sauce.id, 1)}
                      >
                        <Plus className="h-4 w-4" style={{ color: BRAND_COLORS.sunsetOrange }} />
                      </button>
                    </div>
                  </div>
                );
              })}

              <p className="text-xs text-center" style={{ color: BRAND_COLORS.darkSlate + "66" }}>
                You can skip sauces or add as many as you&apos;d like
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 pt-2 border-t border-slate-100 flex gap-2">
          {step > 0 && (
            <Button data-testid="wizard-back-btn" variant="outline" className="flex-1 h-12 rounded-xl font-semibold" onClick={() => setStep(step - 1)}>
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
          )}
          {step < totalSteps - 1 ? (
            <Button 
              data-testid="wizard-next-btn" 
              className="flex-1 h-12 rounded-xl font-bold text-white" 
              style={{ backgroundColor: BRAND_COLORS.sunsetOrange }} 
              disabled={!canNext()} 
              onClick={() => setStep(step + 1)}
            >
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button 
              data-testid="wizard-confirm-btn" 
              className="flex-1 h-12 rounded-xl font-bold text-white" 
              style={{ backgroundColor: BRAND_COLORS.sunsetOrange }} 
              disabled={!canNext()} 
              onClick={handleConfirm}
            >
              Add to Cart {selectedMain && `- ${formatPrice(selectedMain.price + getExtraSauceCost())}`}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
